import { useState } from "react";
import { Button, Card, Space, Tag, Text, Tooltip, useToast } from "@app/ui";
import { registerPassportInRegistry } from "../api/dpp.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { MetaRow } from "./MetaRow.js";
import type { PassportDetail, RegistryRegistrationStatus } from "../types.js";

const REGISTRY_STATUS_COLOR: Record<RegistryRegistrationStatus, "success" | "warning" | "error"> = {
  pending: "warning",
  // Accepted by the registry, still being validated asynchronously — not an
  // outcome yet, so it reads like "pending" rather than success.
  processing: "warning",
  registered: "success",
  failed: "error",
};

/** EU DPP Registry state and the (re)register action. Render only when the feature is configured. */
export function PassportRegistryCard({
  orgId,
  passport,
  canPublish,
  onChanged,
  onError,
}: {
  orgId: string;
  passport: PassportDetail;
  canPublish: boolean;
  onChanged: () => void;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const toast = useToast();
  const fmtDate = useFormatDateTime();
  const [registering, setRegistering] = useState(false);
  const registration = passport.registry.registration;
  const isPublished = passport.status === "published";

  const register = async () => {
    setRegistering(true);
    try {
      // A failed attempt is a 200 with status "failed" — surface it as a toast,
      // the reloaded status line shows lastError in detail.
      const result = await registerPassportInRegistry(orgId, passport.id);
      if (result.status === "registered") toast.success(t("passport.registered"));
      else toast.error(t("passport.registerFailed"));
      onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setRegistering(false);
    }
  };

  return (
    <Card
      title={t("passport.registry")}
      extra={
        canPublish && registration?.status !== "registered" ? (
          <Button size="sm" loading={registering} disabled={!isPublished} onClick={() => void register()}>
            {t("passport.register")}
          </Button>
        ) : undefined
      }
    >
      {registration ? (
        <dl className="divide-y divide-border">
          <MetaRow label={t("common.status")}>
            {registration.status === "failed" && registration.lastError ? (
              <Tooltip title={registration.lastError}>
                <Tag color="error">{t("registryStatus.failed")}</Tag>
              </Tooltip>
            ) : (
              <Tag color={REGISTRY_STATUS_COLOR[registration.status]}>
                {t(`registryStatus.${registration.status}`)}
              </Tag>
            )}
          </MetaRow>
          {registration.registeredAt !== null && (
            <MetaRow label={t("passport.registeredAt")}>{fmtDate(registration.registeredAt)}</MetaRow>
          )}
          {registration.status === "failed" && (
            <MetaRow label={t("passport.registryAttempts")}>{registration.attempts}</MetaRow>
          )}
          <MetaRow label={t("passport.registryUid")}>
            <Text code copyable={{ text: registration.uid }}>
              {registration.uid}
            </Text>
          </MetaRow>
        </dl>
      ) : (
        <Space direction="vertical" size="sm">
          <Tag>{t("passport.notRegistered")}</Tag>
          {!isPublished && (
            <Text small secondary>
              {t("passport.registerHint")}
            </Text>
          )}
        </Space>
      )}
    </Card>
  );
}
